import {
  TripSortCriteria,
  GetGoodProfileListQuery,
  AssetStatus,
  NewGoodProfileInput,
  CustomFieldContext,
  useGetBusinessEntityDetailsQuery,
} from "../../../graphql/generated";
import { TableField } from "../../common/LynksTable/LynksTable";
import SmartLynksTable, {
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
import ExcelImporter from "../../common/ExcelImporter";
import goodProfileSchema from "../GoodProfileForm/goodProfileSchema";
import goodProfilesMapping from "../../../utils/mapping/goodProfiles";
import { Box, Button, Chip, Stack } from "@mui/material";
import { formatWeightForUnit } from "../../../utils/conversion/weight";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import BusinessEntityList from "../../asset-management/BusinessEntityList";
import businessEntityLabel from "../../../utils/labels/businessEntityLabel";
import enumLabel from "../../../utils/labels/enumLabel";
import { testIds } from "./GoodProfilesList.testIds";

type GoodProfileListItem = GetGoodProfileListQuery["goodProfiles"]["data"][0];

export default function GoodProfilesList({
  query,
  onBulkCreate,
}: {
  query: QueryFn<GoodProfileListItem, "goodProfiles", TripSortCriteria>;
  onBulkCreate: (data: NewGoodProfileInput[]) => Promise<any>;
}) {
  const { t } = useTranslation(["assets", "common", "business"]);

  const fields: TableField<GoodProfileListItem, AssetStatus>[] = useMemo(
    () => [
      {
        value: "code",
        label: t("commodity.code", "Commodity code"),
        type: "string",
      },
      {
        value: (goodProfile) => goodProfile.goodProfileClass?.label,
        label: t("commodity.class", "Commodity Class"),
        type: "string",
      },
      {
        value: "label",
        label: t("commodity.label", "Label"),
        type: "string",
      },
      {
        value: (goodProfile) => goodProfile.status || AssetStatus.Active,
        label: t("assets:status", "Status"),
        valueLabel: (status) => t(`common:statusTypes.${status}`),
        type: "enum",
        colors: {
          ACTIVE: "primary",
          INACTIVE: "default",
        },
        values: Object.values(AssetStatus),
        sortBy: "status",
      },
      {
        value: (goodProfile) => goodProfile.unit,
        label: t("commodity.unit", "Unit"),
        type: "string",
        sortBy: "unit",
      },
      {
        value: (goodProfile) => enumLabel(goodProfile.productType) ?? "",
        label: t("commodity.productType", "Product Type"),
        type: "string",
        sortBy: "productType",
      },
      {
        value: (goodProfile) =>
          formatWeightForUnit(goodProfile.weight, goodProfile.unit),
        label: t("commodity.weight", "Weight"),
        type: "number",
        sortBy: "weight",
      },
      {
        value: "liquidGravity",
        label: t("commodity.gravity", "Gravity"),
        type: "number",
        sortBy: "liquidGravity",
      },
      {
        value: (goodProfile) => (
          <Stack direction="row" spacing={1}>
            {goodProfile.tags?.map((tag) => (
              <Chip label={tag} key={tag} />
            ))}
          </Stack>
        ),
        sortBy: "tags",
        label: t("common:tags"),
        type: "string",
      },
      {
        value: (goodProfile) => {
          return (
            <BusinessEntityList entityIds={goodProfile.shipperIds} inline />
          );
        },
        // Synchronous value for the grid (sort/filter) so rendering never
        // fetches entities. Name resolution happens only on CSV export.
        rawValue: (goodProfile) => (goodProfile.shipperIds || []).join(", "),
        exportValue: async (goodProfile) => {
          if (!goodProfile.shipperIds || goodProfile.shipperIds.length === 0) {
            return "";
          }

          const labels = await Promise.all(
            goodProfile.shipperIds.map(async (id) => {
              try {
                const result = await useGetBusinessEntityDetailsQuery.fetcher({ id })();
                return result.businessEntityById
                  ? businessEntityLabel(result.businessEntityById)
                  : id;
              } catch {
                return id;
              }
            })
          );

          return labels.join(", ");
        },
        label: capitalize(t("business:shipper.many")),
        type: "string",
      },
      {
        value: (goodProfile) => {
          return (
            <BusinessEntityList entityIds={goodProfile.supplierIds} inline />
          );
        },
        // Synchronous value for the grid (sort/filter) so rendering never
        // fetches entities. Name resolution happens only on CSV export.
        rawValue: (goodProfile) => (goodProfile.supplierIds || []).join(", "),
        exportValue: async (goodProfile) => {
          if (!goodProfile.supplierIds || goodProfile.supplierIds.length === 0) {
            return "";
          }

          const labels = await Promise.all(
            goodProfile.supplierIds.map(async (id) => {
              try {
                const result = await useGetBusinessEntityDetailsQuery.fetcher({ id })();
                return result.businessEntityById
                  ? businessEntityLabel(result.businessEntityById)
                  : id;
              } catch {
                return id;
              }
            })
          );

          return labels.join(", ");
        },
        label: capitalize(t("business:supplier.many")),
        type: "string",
      },
    ],
    [t]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          height: "calc(100% - 74px)",
        }}
      >
        <SmartLynksTable
          query={query}
          dataKey="goodProfiles"
          fields={fields}
          detailsUrlPrefix="/good-profiles/details"
          customFieldContext={[CustomFieldContext.Commodity]}
          selectable
          showGroups
          renderHeaderActions={() => (
            <Stack direction="row" spacing={2}>
              <ExcelImporter
                mapping={goodProfilesMapping}
                schema={goodProfileSchema}
                onBulkCreate={onBulkCreate}
              />
              <Button data-testid={testIds.goodProfilesListNewCommodity}
                href="/good-profiles/new"
                variant="contained"
                color="secondary"
                size="large"
                id="new-goodProfile-button"
              >
                {t("commodity.new", "New Commodity")}
              </Button>
            </Stack>
          )}
        />
      </Box>
    </div>
  );
}
